import type { Ingredient } from '@/components/recipes/IngredientList';

const NON_ALLERGEN_VALUES = new Set(['none', 'no', 'n_a', 'na', 'nil', 'unknown']);

const ALLERGEN_ALIASES: Record<string, string> = {
  peanut: 'peanuts',
  peanuts: 'peanuts',
  tree_nut: 'tree_nuts',
  tree_nuts: 'tree_nuts',
  treenut: 'tree_nuts',
  treenuts: 'tree_nuts',
  egg: 'eggs',
  eggs: 'eggs',
  milk: 'dairy',
  dairy: 'dairy',
  dairy_products: 'dairy',
  shell_fish: 'shellfish',
  shellfish: 'shellfish',
  sulphite: 'sulfite',
  sulphites: 'sulfite',
  sulfites: 'sulfite',
  wheat: 'gluten',
};

const INTOLERANCE_ALIASES: Record<string, string> = {
  lactose: 'lactose',
  casein: 'casein',
  gluten: 'gluten_intolerance',
  gluten_intolerance: 'gluten_intolerance',
  wheat: 'gluten_intolerance',
  fructose: 'fructose',
  fodmap: 'fodmap',
  low_fodmap: 'fodmap',
  nightshade: 'nightshade',
  lectin: 'lectin',
  salicylate: 'salicylate',
  histamine: 'histamine',
  oxalate: 'oxalate',
  msg: 'msg',
  monosodium_glutamate: 'msg',
  caffeine: 'caffeine',
  sulphite: 'sulfite_intolerance',
  sulphites: 'sulfite_intolerance',
  sulfite: 'sulfite_intolerance',
  sulfites: 'sulfite_intolerance',
  sulfite_intolerance: 'sulfite_intolerance',
  corn: 'corn_intolerance',
  maize: 'corn_intolerance',
  corn_intolerance: 'corn_intolerance',
  yeast: 'yeast',
  tannin: 'tannin',
};

function normalizeRecipeTagValue(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function normalizeRecipeAllergen(value: unknown): string {
  const normalized = normalizeRecipeTagValue(value);

  if (!normalized || NON_ALLERGEN_VALUES.has(normalized)) return '';
  return ALLERGEN_ALIASES[normalized] || normalized;
}

export function normalizeRecipeIntolerance(value: unknown): string {
  const normalized = normalizeRecipeTagValue(value);

  if (!normalized || NON_ALLERGEN_VALUES.has(normalized)) return '';
  return INTOLERANCE_ALIASES[normalized] || normalized;
}

export function normalizeRecipeAllergenList(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  const allergens = new Set<string>();
  values.forEach((value) => {
    const normalized = normalizeRecipeAllergen(value);
    if (normalized) allergens.add(normalized);
  });
  return Array.from(allergens).sort();
}

export function normalizeRecipeIntoleranceList(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  const intolerances = new Set<string>();
  values.forEach((value) => {
    const normalized = normalizeRecipeIntolerance(value);
    if (normalized) intolerances.add(normalized);
  });
  return Array.from(intolerances).sort();
}

export function mergeRecipeAllergens(...values: unknown[]): string[] {
  const allergens = new Set<string>();
  values.forEach((value) => {
    normalizeRecipeAllergenList(value).forEach((allergen) => allergens.add(allergen));
  });
  return Array.from(allergens).sort();
}

export function mergeRecipeIntolerances(...values: unknown[]): string[] {
  const intolerances = new Set<string>();
  values.forEach((value) => {
    normalizeRecipeIntoleranceList(value).forEach((intolerance) => intolerances.add(intolerance));
  });
  return Array.from(intolerances).sort();
}

export function deriveAllergensFromIngredients(ingredients: Ingredient[]): string[] {
  return mergeRecipeAllergens(
    ...ingredients.flatMap((ingredient) => [
      ingredient.allergenTagIds ?? [],
      ingredient.allergens ?? [],
    ]),
  );
}

export function deriveIntolerancesFromIngredients(ingredients: Ingredient[]): string[] {
  return mergeRecipeIntolerances(
    ...ingredients.flatMap((ingredient) => [
      ingredient.intoleranceTagIds ?? [],
      ingredient.intoleranceTags ?? [],
    ]),
  );
}
